import type { LucideIcon } from 'lucide-react';
import { Icons } from '@/shared/constants/icons';
import { ROUTES } from '@/shared/constants/routes';
import type { PlatformId } from '@/shared/components/PlatformIcon';

export type Group = 'ask' | 'recent' | 'conversations' | 'posts' | 'occasions' | 'actions' | 'pages' | 'settings';
// Ask comes last: a typed page name still jumps on Enter; with nothing found, asking is what is left.
export const GROUP_ORDER: readonly Group[] = ['recent', 'conversations', 'posts', 'occasions', 'actions', 'pages', 'settings', 'ask'];

export interface PaletteItem {
  id: string;
  group: Group;
  label: string;
  detail?: string;
  icon?: LucideIcon;
  platform?: PlatformId;
  emoji?: string;
  shortcut?: string;
  /** Extra words the search matches on. */
  keywords?: string;
  run: () => void;
  /** A route to remember in Recent; actions without one are not remembered. */
  to?: string;
}

export interface RecentEntry {
  id: string;
  label: string;
  detail?: string;
  to: string;
  platform?: PlatformId;
  emoji?: string;
}

export const RECENT_KEY = 'unipostudio.palette.recent';
const RECENT_MAX = 5;
export const BROWSE_MAX = 4;
export const SEARCH_MAX = 8;
export const OCCASION_HORIZON_DAYS = 90;

export function readRecent(): RecentEntry[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as RecentEntry[]).filter((r) => r && typeof r.to === 'string' && typeof r.label === 'string') : [];
  } catch {
    return [];
  }
}

export function writeRecent(entry: RecentEntry): void {
  try {
    const next = [entry, ...readRecent().filter((r) => r.id !== entry.id)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable: recents are a convenience only */
  }
}

/** Brand profile fields the palette can jump to (a stand-in for the knowledge index). */
export const BRAND_FIELDS: { key: string; section: string; keywords: string }[] = [
  { key: 'name', section: 'identity', keywords: 'brand name' },
  { key: 'tagline', section: 'identity', keywords: 'tagline slogan' },
  { key: 'logo', section: 'identity', keywords: 'logo' },
  { key: 'colours', section: 'identity', keywords: 'colours colors accent' },
  { key: 'hours', section: 'details', keywords: 'opening hours open closed times' },
  { key: 'address', section: 'details', keywords: 'address location where' },
  { key: 'phone', section: 'details', keywords: 'phone number call' },
  { key: 'email', section: 'details', keywords: 'email mail' },
  { key: 'website', section: 'links', keywords: 'website url site' },
  { key: 'services', section: 'services', keywords: 'services menu offer' },
  { key: 'prices', section: 'services', keywords: 'prices pricing cost' },
  { key: 'tone', section: 'voice', keywords: 'tone voice style' },
  { key: 'languages', section: 'voice', keywords: 'languages language' },
  { key: 'faq', section: 'knowledge', keywords: 'faq knowledge questions answers' },
  { key: 'links', section: 'links', keywords: 'links socials social' },
  { key: 'team', section: 'team', keywords: 'team people staff' },
  { key: 'audience', section: 'audience', keywords: 'audience customers' },
];

/**
 * Settings the palette can open. `label` is an i18n key when the palette's
 * own word is not what the page calls itself; `keywords` are the other words
 * people type for it (F35: "billing", "invoice", "2fa" found nothing).
 */
export const SETTINGS_TABS: { key: string; tab: string; label?: string; keywords?: string }[] = [
  { key: 'profile', tab: 'profile', keywords: 'name photo avatar account me' },
  { key: 'language', tab: 'language', keywords: 'language translate region locale' },
  { key: 'notifications', tab: 'notifications', keywords: 'email alerts push digest' },
  { key: 'security', tab: 'security', keywords: 'password 2fa two-factor two factor mfa authenticator code sessions devices login sign-in' },
  { key: 'privacy', tab: 'account', keywords: 'privacy data delete export cookies consent gdpr' },
  { key: 'team', tab: 'team', keywords: 'members invite seats roles people' },
  { key: 'branding', tab: 'branding', keywords: 'white label logo favicon' },
  { key: 'activityLog', tab: 'activity-log', keywords: 'audit history log' },
  { key: 'inboxSla', tab: 'inbox-sla', keywords: 'sla response time' },
  {
    key: 'subscription',
    tab: 'subscription',
    label: 'settings.nav.billing',
    keywords: 'billing plan plans upgrade downgrade invoice invoices receipt subscription credits payment pay card price pricing gst cancel pro agency',
  },
  { key: 'api', tab: 'api', keywords: 'api keys developer' },
  { key: 'webhooks', tab: 'webhooks', label: 'settings.nav.webhooks', keywords: 'webhooks developer zapier make endpoint events' },
];

/** Pages mirror the rail exactly (DS04); Team is a Settings tab, found under Settings. */
export const PAGES: { key: string; to: string; icon: LucideIcon; keywords?: string }[] = [
  { key: 'overview', to: ROUTES.OVERVIEW, icon: Icons.home, keywords: 'overview dashboard attention problems failed' },
  { key: 'inbox', to: ROUTES.INBOX, icon: Icons.inbox },
  { key: 'calendar', to: ROUTES.CALENDAR, icon: Icons.calendar },
  { key: 'plan', to: ROUTES.PLAN, icon: Icons.plan },
  { key: 'posts', to: ROUTES.POSTS, icon: Icons.posts },
  { key: 'approvals', to: ROUTES.APPROVALS, icon: Icons.check },
  { key: 'media', to: ROUTES.MEDIA, icon: Icons.media },
  { key: 'templates', to: ROUTES.TEMPLATES, icon: Icons.templates },
  { key: 'analytics', to: ROUTES.ANALYTICS, icon: Icons.analytics },
  { key: 'feeds', to: ROUTES.FEEDS, icon: Icons.feeds },
  { key: 'brand', to: ROUTES.BRAND, icon: Icons.brand },
  { key: 'channels', to: ROUTES.CHANNELS, icon: Icons.channels },
  { key: 'help', to: ROUTES.DOCS, icon: Icons.help },
];

export const preview = (text: string, max = 64): string => {
  const t = text.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

export function matches(item: PaletteItem, tokens: string[]): boolean {
  if (tokens.length === 0) return true;
  const hay = `${item.label} ${item.detail ?? ''} ${item.keywords ?? ''}`.toLowerCase();
  return tokens.every((tk) => hay.includes(tk));
}

/** Opens the brand switcher (M20) the same way the keyboard does, after the palette has closed. */
export function openBrandSwitcher(): void {
  window.setTimeout(() => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'b', ctrlKey: true, bubbles: true, cancelable: true }));
  }, 0);
}

